import { useState, useEffect } from 'react';

/**
 * Returns live countdown for a video consultation slot.
 * status: 'waiting' | 'active' | 'expired'
 * remaining: seconds left in active window (or until start if waiting)
 *
 * slotTime: 'HH:MM' (24h)
 * appointmentDate: 'YYYY-MM-DD' (plain date string in IST)
 * duration: minutes
 */
export function useSlotTimer(slotTime, appointmentDate, duration = 10) {
  const [status,    setStatus]    = useState('waiting');
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!slotTime || !appointmentDate) return;

    // Build start Date in LOCAL time using date parts — avoids UTC/IST mismatch
    const [h, m]        = slotTime.split(':').map(Number);
    const [year, month, day] = appointmentDate.split('-').map(Number);

    // new Date(year, month-1, day, h, m) uses LOCAL timezone — correct on any device/server
    const start = new Date(year, month - 1, day, h, m, 0, 0);
    const end   = new Date(start.getTime() + duration * 60 * 1000);

    const tick = () => {
      const now = Date.now();
      const startMs = start.getTime();
      const endMs   = end.getTime();

      if (now < startMs) {
        setStatus('waiting');
        setRemaining(Math.max(0, Math.floor((startMs - now) / 1000)));
      } else if (now >= startMs && now <= endMs) {
        setStatus('active');
        setRemaining(Math.max(0, Math.floor((endMs - now) / 1000)));
      } else {
        setStatus('expired');
        setRemaining(0);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [slotTime, appointmentDate, duration]);

  return { status, remaining };
}
